import { useEffect, useState } from "react";

/**
 * S3 JSON에서 홈페이지 콘텐츠를 fetch하는 훅
 *
 * 읽기 경로:
 *   REACT_APP_CMS_CONTENT_URL/{page}.json → S3/CDN에서 직접 읽기
 *
 * 미리보기 경로 (어드민 iframe):
 *   postMessage(CMS_PREVIEW) → fetch 없이 메모리에서 실시간 반영
 *   postMessage(CMS_SCROLL_TO) → 해당 섹션으로 스크롤
 *
 * @param {string} page - 콘텐츠 페이지 키 ('home' | 'products' | ...)
 * @param {object} fallback - fetch 실패 시 사용할 기본값 (homeLang.js 값)
 * @returns {object} 현재 콘텐츠 (fetch 성공 시 S3 값, 실패 시 fallback)
 */
export function useCmsContent(page, fallback) {
  const [content, setContent] = useState(fallback);

  // ① S3/CDN에서 JSON fetch
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_CMS_CONTENT_URL;

    // env 미설정 시 fallback 그대로 사용 (로컬 개발)
    if (!baseUrl) return;

    // 성능 측정: 콘텐츠 fetch 비용을 User Timing으로 기록
    // (Lighthouse "User Timing" 섹션 / PerformanceObserver / DevTools에서 확인 가능)
    const startMark = `cms-fetch-start:${page}`;
    const endMark = `cms-fetch-end:${page}`;
    const measureName = `cms-content-fetch:${page}`;
    const canMeasure =
      typeof performance !== "undefined" && typeof performance.mark === "function";

    if (canMeasure) performance.mark(startMark);

    fetch(`${baseUrl}/${page}.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`[CMS] ${page}.json 응답 오류: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (data?.content) {
          // fallback과 병합 — JSON에 없는 필드는 fallback 값 유지
          setContent((prev) => ({ ...prev, ...data.content }));
        }
      })
      .catch((err) => {
        // fetch 실패 시 fallback 유지 (무중단 보장)
        console.warn(`[CMS] ${page}.json fetch 실패 — fallback 사용`, err.message);
      })
      .finally(() => {
        if (!canMeasure) return;
        performance.mark(endMark);
        try {
          performance.measure(measureName, startMark, endMark);
          const entry = performance.getEntriesByName(measureName).at(-1);
          if (entry) {
            // eslint-disable-next-line no-console
            console.info(`[CMS perf] ${measureName}: ${entry.duration.toFixed(1)}ms`);
          }
        } catch {
          /* measure 실패는 무시 (성능 측정은 부가 기능) */
        }
      });
  }, [page]);

  // ② 어드민 iframe → postMessage 수신
  useEffect(() => {
    const handler = (e) => {
      // 어드민에서 실시간 미리보기 데이터 전송
      if (e.data?.type === "CMS_PREVIEW" && e.data?.page === page) {
        setContent((prev) => ({ ...prev, ...e.data.content }));
      }

      // 어드민 섹션 이동 버튼 클릭 시 해당 섹션으로 스크롤
      if (e.data?.type === "CMS_SCROLL_TO" && e.data?.section) {
        // cms-<section> 우선, 없으면 raw id(예: 랜딩 카테고리 satellite_system) fallback
        const el =
          document.getElementById(`cms-${e.data.section}`) ||
          document.getElementById(e.data.section);
        el?.scrollIntoView({ behavior: "smooth" });
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [page]);

  return content;
}
